/**
 * absentFactDomain — is a number in the narrative actually about the thing the
 * ABSENT fact governs?
 *
 * ── The defect this cures ────────────────────────────────────────────────────
 * The absent-leakage arm of `checkNumericGrounding` blocks any tagged number
 * whose unit has no present fact but does have an ABSENT one. `grid.queueYears`
 * (interconnection queue) is the only fact carrying unit `years`, and on a site
 * with no utility queue estimate it is absent — so EVERY duration anywhere in the
 * report was accused of being invented.
 *
 * Measured live (Redhead, 2026-08-16): 38 `invented-number` blocks across 4
 * reports, holding all of them. Every sampled excerpt was a different domain:
 *   "This is a critical-path permit that typically takes 6-12 months…"   §404
 *   "A major source PSD air permit … typically takes 12-18 months…"      air
 *   "This process can take 3-6 months … mandatory conservation measures" water
 *   "This analysis of public sentiment over the last 12 months…"         lookback
 * None was an interconnection queue estimate. Each block was a FALSE ACCUSATION
 * against a true statement, and each one gated its report.
 *
 * ── Why a POSITIVE marker is required, rather than a blacklist ───────────────
 * A blacklist of non-queue domains cannot work: the live set includes bare
 * ranges ("18-30 months") and market lookbacks ("over the last 12 months") that
 * carry no domain word at all. More fundamentally, the burden belongs on the
 * accusation. When a fact has a VALUE, matching is evidence — the number either
 * equals it or it does not. When the fact is ABSENT there is no value, so the
 * ONLY link to the mention is a shared unit. For a unit as domain-generic as
 * duration (permitting, construction, procurement, finance, market history) that
 * link cannot sustain a block.
 *
 * So: an absent duration fact may accuse a number ONLY when the surrounding text
 * ties it to that fact's domain. See [[mw_polysemy_shared_predicate]]:
 * mis-governing manufactures an accusation and makes a report unfixable;
 * un-governing merely misses one.
 *
 * ── THE RESIDUAL, STATED ACCURATELY ─────────────────────────────────────────
 * An earlier draft of this comment claimed the declined case "surfaces as
 * advisory instead of a block". THAT WAS WRONG, and adversarial review caught it
 * against the running code: when the unit's only fact is absent there is no
 * present same-unit fact, so `checkNumericGrounding` returns before BOTH the
 * fall-through `unlabeled-estimate` warn and the absent-assertion arm. A declined
 * mention produces NOTHING — it is silent, not advisory. No other engine covers
 * it either: the R-79 reconciler extracts capacity/distance/acreage but no
 * durations, and the absence detector has no duration handling.
 *
 * That makes the window's shape load-bearing, which is why the caller passes the
 * enclosing SERIALIZED RECORD LINE when there is one (see `governanceWindow`).
 * The remaining known miss: free prose that names the queue in one sentence and
 * states the figure in the next ("The utility has not published a queue
 * estimate. Assume a 5 year wait."). Widening further — to the paragraph, or
 * inheriting the section heading — is NOT safe: a permit timeline inside an
 * `# interconnectionAnalysis` section would be re-accused, which is exactly the
 * false-positive class this module exists to cure.
 *
 * ── What this is NOT ─────────────────────────────────────────────────────────
 * This is NOT the reverted v1.24.0 subject classifier. That filtered CANDIDATE
 * FACTS by inferred subject before matching, so it could delete the very fact a
 * figure equalled and then block it against a survivor. This decides only whether
 * an ABSENT fact — one with no value to match against at all — is entitled to
 * speak. No candidate is ever removed, because there are no candidates.
 */

/** Units that name a duration. */
export const DURATION_UNITS: ReadonlySet<string> = new Set(['years', 'year', 'months', 'month']);

/** Units that name a distance. */
export const DISTANCE_UNITS: ReadonlySet<string> = new Set(['km', 'mi', 'miles', 'mile', 'm']);

/**
 * Vocabulary that ties a DISTANCE to one of the four governed "nearest X" facts:
 * `substationDistKm`, `ix.distanceKm` (IX / fibre), `reactor.distanceKm`,
 * `pipeline.distanceKm`.
 *
 * Live (2026-08-17, first v1.25.1 corpus run): all four were ABSENT, so every
 * distance in the report was accused — 12 of 18 invented-number blocks, the
 * single largest remaining false-positive class. The blocked prose was
 * TRANSMISSION LINE geometry:
 *   "an existing Entergy 500kV line (1.43 km away), a proposed 230kV line
 *    (0.11 km away), an existing 1803 69kV line crossing the parcel"
 * A kV line is not a substation, an IX, a reactor or a pipeline. It has NO
 * governed distance fact, so nothing in the store can adjudicate it and an
 * absent fact must not pretend to.
 *
 * Fibre/IX vocabulary IS included: `ix.distanceKm` governs that domain, so a
 * stated fibre distance with no fact behind it stays a legitimate catch.
 */
const DISTANCE_DOMAIN_RE = new RegExp(
  [
    'substation',
    'interconnect\\w*', 'point of interconnection', '\\bPOI\\b',
    '\\bIX\\b', 'internet exchange', 'fib(?:re|er)', 'long[- ]haul',
    'reactor', 'nuclear plant', '\\bSMR\\b',
    'pipeline', 'gas main', 'gas lateral',
  ].join('|'),
  'i',
);

/** True when a distance mention is tied to a domain the store actually governs. */
export function isGovernedDistance(window: string): boolean {
  return DISTANCE_DOMAIN_RE.test(window);
}

/**
 * Vocabulary that ties a duration to the INTERCONNECTION QUEUE specifically —
 * the domain `grid.queueYears` governs. Directly qualified non-grid
 * interconnections are excluded only when no other queue/grid term is present.
 *
 * Deliberately excludes bare "study" and bare "process": both appear constantly
 * in permitting prose ("alternatives analysis", "the process to secure these
 * rights") and would re-admit the false positives this module exists to stop.
 */
const QUEUE_DOMAIN_RE = new RegExp(
  [
    'queue',
    '\\bLGIA\\b', '\\bSIS\\b', '\\bGIA\\b',
    'system impact study',
    'facilit\\w+ study',
    'transmission service',
    'cluster (?:study|window)',
    'point of interconnection', '\\bPOI\\b',
    '\\bISO\\b', '\\bRTO\\b', '\\bMISO\\b', '\\bERCOT\\b', '\\bPJM\\b', '\\bSPP\\b', '\\bCAISO\\b',
    'utility (?:study|review|process)',
    'energization', 'energiz\\w+',
    'grid connection', 'connection agreement',
  ].join('|'),
  'i',
);

/**
 * True when a duration mention sits in text that ties it to the interconnection
 * queue, and an absent queue fact may therefore govern it.
 *
 * `window` is the text considered around the mention. Callers pass the sentence;
 * a sentence is the smallest unit that reliably carries the domain word, and
 * widening to a paragraph re-admits cross-topic bleed (a permitting sentence
 * adjacent to an interconnection sentence).
 */
export function isQueueGovernedDuration(window: string): boolean {
  if (QUEUE_DOMAIN_RE.test(window)) return true;
  const hits = [...window.matchAll(/\binterconnect\w*/gi)];
  if (!hits.length) return false;
  // Wording-only domain distinction: a gas/fuel lateral is not an electrical
  // queue. Any mixed grid/load/utility-service wording retains governance.
  if (/\bgrid\b|\belectric(?:al|ity)?\b|\bsubstation\b|\bkV\b|\bload[- ]service\b|\butility\s+(?:interconnection|connection|service)\b|\bpower\s+(?:line|grid)\b|\binterconnect\w*\s+(?:with|to)\s+(?:the\s+)?utility\b/i.test(window)) return true;
  return hits.some(hit => {
    const before = window.slice(0, hit.index);
    const after = window.slice(hit.index! + hit[0].length);
    const nonGridBefore = /\b(?:gas|pipeline|fuel|fib(?:re|er)|water|sewer)(?:\s+[\w-]+)?\s*$/i.test(before);
    const qualifiedLateral = /\blateral(?:\s+[\w-]+)?\s*$/i.test(before)
      && /\b(?:gas|pipeline|fuel|firm\s+transportation\s+service)\b/i.test(window);
    const nonGridAfter = /^\s+(?:with|to|into)\s+(?:the\s+)?(?:[\w-]+\s+){0,4}(?:pipeline|gas\s+main|gas\s+system|gas\s+transmission)\b/i.test(after);
    return !nonGridBefore && !qualifiedLateral && !nonGridAfter;
  });
}

/**
 * Should an ABSENT fact be allowed to accuse this mention of being invented?
 *
 * Non-duration units are unaffected (returns true) — this module makes no claim
 * about MW, acres, km or currency, whose absent-leakage behaviour is unchanged.
 */
export function absentFactMayGovern(unit: string | undefined, window: string, factId?: string): boolean {
  if (!unit) return true;
  const u = unit.toLowerCase();
  if (DURATION_UNITS.has(u)) {
    if (factId === 'finance.paybackYears') return /\bpayback\b/i.test(window);
    if (factId === 'finance.projectLifeYears') return /\b(?:project|design|operating|asset)\s+life(?:time)?\b/i.test(window);
    return isQueueGovernedDuration(window);
  }
  if (DISTANCE_UNITS.has(u)) return isGovernedDistance(window);
  // Stored battery energy is not the project's annual electrical production.
  // A mixed or explicitly annual claim remains governed by the annual fact.
  if (u === 'mwh' && factId === 'finance.annualMwh'
    && /\b(?:BESS|battery|storage)\b/i.test(window)
    && !/\b(?:annual|annually|yearly|per\s+year|year[- ]one)\b/i.test(window)) return false;
  // Units with a dominant referent (MW, acres, currency) keep their existing
  // absent-leakage behaviour — this module makes no claim about them.
  return true;
}

/**
 * The text an absent duration fact is judged against.
 *
 * A sentence is the right unit for free prose, but reports render structured
 * content as ONE RECORD PER LINE — `- Title: … | Detail: … | Source: …` — and a
 * `. ` inside `Detail` splits that row. Adversarial review demonstrated the
 * consequence on the live golden case: `- Title: Interconnection queue | Detail:
 * 12-24 months …` blocks only because the figure happens to sit in the row's
 * FIRST sentence. Insert one sentence before it and the identical row goes
 * silent — so the single live catch was passing for a positional reason, and any
 * repair regeneration that nudged the figure past a period would have laundered
 * it away.
 *
 * Widening to the whole line when (and only when) the line is a serialized
 * record fixes that without re-admitting anything: `isSerializedRecord` requires
 * two or more `Key: ` segments, a shape ordinary prose never takes, and none of
 * the six live false positives this module cures is a record.
 *
 * ── PROSE USES PROXIMITY, NOT SENTENCE MEMBERSHIP (2026-08-17) ──────────────
 * The non-record path receives a PROXIMITY slice (see `proximityAt` in
 * groundingValidator), not the whole sentence. Sentence membership was too
 * coarse: one live sentence enumerated four assets —
 *   "an existing Entergy 500kV line (1.43 km away), a proposed 230kV line
 *    (0.11 km away), … and the Tennessee Gas Pipeline crossing the parcel"
 * — so `Pipeline`, 124 characters away, lent its governed domain to every
 * kV-line distance and blocked all of them.
 *
 * This is the THIRD time the same shape appeared: a marker in scope-X taken as
 * evidence about a quantity that merely SHARES scope-X (a discursive row title
 * governing the user's 2-5 year objective; a pipeline governing a line's
 * distance; and, one revert earlier, the v1.24.0 nearest-marker classifier).
 * Proximity is the general answer, and it is exactly how `hasNearbyQualifier`
 * has always worked.
 */
export function governanceWindow(
  proximity: string,
  line: string | undefined,
  isRecord: (text: string) => boolean,
): string {
  if (line && isRecord(line) && hasQueueSubjectTitle(line)) return line;
  return proximity;
}

/**
 * Does this record row's TITLE name the interconnection queue as its SUBJECT?
 *
 * Row widening cannot simply ask whether the row mentions interconnection
 * anywhere. Measured against the 5 clean live reports before shipping: a mere
 * `isQueueGovernedDuration(line)` test newly blocked 3 of them, and every hit was
 * the SAME quantity — the user's declared "2-5 year time-to-market objective",
 * sitting in a row whose title happened to read "Severe Grid Interconnection
 * Uncertainty…" or "Critically Deficient Grid Infrastructure…". One row was
 * literally `Intent Context: … · 2–5 yr`.
 *
 * That is a variant of the defect that forced the v1.24.0 revert, where 60 of 90
 * false blocks landed on the platform's own Intent Context boilerplate. A
 * discursive title that merely MENTIONS interconnection is not evidence that a
 * duration elsewhere in the row is a queue estimate.
 *
 * The live true positive is distinguishable without heuristics: its title is
 * `Interconnection queue` — the governed fact's own label. So the widening
 * requires the title to BE a queue reference, not to contain one.
 */
function hasQueueSubjectTitle(line: string): boolean {
  const segments = line.replace(/^-\s+/, '').split(' | ');
  const title = segments.find((s) => /^title:\s/i.test(s));
  if (!title) return false;
  const value = title.replace(/^title:\s*/i, '').trim();
  return /^(?:grid\s+|utility\s+)?(?:interconnection\s+)?queue\b/i.test(value)
    || /^interconnection\s+(?:queue|timeline|schedule)\b/i.test(value);
}

/** The full line containing `index`, bounded by hard line breaks. */
export function lineAt(text: string, index: number): string {
  const start = text.lastIndexOf('\n', Math.max(0, index - 1)) + 1;
  const endRel = text.indexOf('\n', index);
  return text.slice(start, endRel === -1 ? text.length : endRel);
}
